/**
 * O complemento com destino por CCB (E177.F6, GUS-253, ADR-0130; HTML 6.8 por CCB
 * `?tela=complemento-ccb`), no portal e na equipe.
 *
 * - **"A quais CCBs estes documentos se referem?"** — este caso marcado e fixo; as CCBs dos outros
 *   casos eletrônicos da mesma pasta e processo, para marcar. Sem outra CCB, nada aparece.
 * - **Por lote**: as CCBs marcadas ao escolher os arquivos valem para aquele lote; selado o
 *   recebimento, ele é encaminhado a cada uma pelos mesmos bytes (um recebimento novo em cada caso,
 *   com a origem). A linha diz para onde foi; a falha oferece tentar de novo — os documentos ficaram
 *   neste caso.
 * - **E177.F6.C02 (F-E177.F6-02)**: as CCBs do lote ficam em cada arquivo dele (`onLote`), como os
 *   dados do complemento (`useDadosDoLote`); o recebimento resolve as CCBs pelos arquivos que abriu
 *   (`onAberto`). A retentativa que abre outro recebimento leva as CCBs do lote do arquivo, não as do
 *   último lote escolhido.
 * - **E177.F6.C01 (F-E177.F6-01)**: só é "enviado" o que a resposta confirma: a CCB escolhida que não
 *   volta entre os destinos é falha, com a nova tentativa.
 */
import { useEffect, useRef, useState } from 'react';
import type { AxiosInstance } from 'axios';

import { ptBR } from '@/i18n/pt-BR';
import {
  encaminharComplemento,
  lerCcbsDoProcesso,
  type CcbsDoProcessoData,
} from '@/lib/complemento_por_ccb';

const t = ptBR.abertura.conferirPorCcb.complemento;
const rotulo = (numero: string): string =>
  ptBR.abertura.contratosDigitais.rotulo.replace('{numero}', numero);

type EstadoDoEncaminhamento = 'enviando' | 'feito' | 'falhou';

export interface EncaminhamentoDoLote {
  recebimentoId: string;
  destinos: readonly string[];
  estado: EstadoDoEncaminhamento;
}

export interface DestinosPorCcb {
  dados: CcbsDoProcessoData | null;
  erro: boolean;
  marcadas: ReadonlySet<string>;
  alternar: (caseId: string, marcada: boolean) => void;
  /** Ao escolher os arquivos: as marcadas passam a valer para o lote. */
  iniciarLote: () => void;
  /** As linhas do lote (`onLote` do envio): cada uma guarda as CCBs do lote. */
  onLote: (localIds: readonly string[]) => void;
  /** O recebimento abriu para estas linhas (`onAberto` do envio): o do lote ou o da retentativa. */
  onAberto: (recebimentoId: string, localIds: readonly string[]) => void;
  /** O recebimento foi selado (`onConcluido` do envio): encaminha às CCBs do lote. */
  encaminhar: (recebimentoId: string) => Promise<void>;
  encaminhamentos: readonly EncaminhamentoDoLote[];
}

export function useDestinosPorCcb(
  client: AxiosInstance,
  caseId: string,
  ativo: boolean,
): DestinosPorCcb {
  const [dados, setDados] = useState<CcbsDoProcessoData | null>(null);
  const [erro, setErro] = useState(false);
  const [marcadas, setMarcadas] = useState<ReadonlySet<string>>(new Set());
  const [encaminhamentos, setEncaminhamentos] = useState<readonly EncaminhamentoDoLote[]>([]);
  // As CCBs do lote escolhido, de cada arquivo e de cada recebimento; não é estado de tela.
  const doLote = useRef<readonly string[]>([]);
  const porLinha = useRef(new Map<string, readonly string[]>());
  const doRecebimento = useRef(new Map<string, readonly string[]>());

  useEffect(() => {
    if (!ativo) return;
    let vivo = true;
    lerCcbsDoProcesso(client, caseId).then(
      (lidos) => vivo && setDados(lidos),
      () => vivo && setErro(true),
    );
    return () => {
      vivo = false;
    };
  }, [client, caseId, ativo]);

  function marcar(
    recebimentoId: string,
    destinos: readonly string[],
    estado: EstadoDoEncaminhamento,
  ) {
    setEncaminhamentos((lista) => [
      ...lista.filter((e) => e.recebimentoId !== recebimentoId),
      { recebimentoId, destinos, estado },
    ]);
  }

  async function encaminhar(recebimentoId: string): Promise<void> {
    const destinos = doRecebimento.current.get(recebimentoId) ?? [];
    if (destinos.length === 0) return;
    marcar(recebimentoId, destinos, 'enviando');
    try {
      const resposta = await encaminharComplemento(client, caseId, recebimentoId, destinos);
      const chegaram = new Set(resposta.destinos.map((d) => d.case_id));
      marcar(recebimentoId, destinos, destinos.every((d) => chegaram.has(d)) ? 'feito' : 'falhou');
    } catch {
      marcar(recebimentoId, destinos, 'falhou');
    }
  }

  return {
    dados,
    erro,
    marcadas,
    alternar: (id, marcada) =>
      setMarcadas((atual) => {
        const nova = new Set(atual);
        if (marcada) nova.add(id);
        else nova.delete(id);
        return nova;
      }),
    iniciarLote: () => {
      doLote.current = [...marcadas];
    },
    onLote: (localIds) => {
      for (const id of localIds) porLinha.current.set(id, doLote.current);
    },
    onAberto: (recebimentoId, localIds) => {
      const destinos = localIds
        .map((id) => porLinha.current.get(id))
        .find((d) => d !== undefined && d.length > 0);
      if (destinos !== undefined && !doRecebimento.current.has(recebimentoId)) {
        doRecebimento.current.set(recebimentoId, destinos);
      }
    },
    encaminhar,
    encaminhamentos,
  };
}

/** "A quais CCBs estes documentos se referem?" — só com outra CCB na mesma pasta e processo. */
export function DestinoPorCcb({
  destinos,
  disabled = false,
}: {
  destinos: DestinosPorCcb;
  disabled?: boolean;
}): JSX.Element | null {
  const { dados, erro } = destinos;
  if (erro) {
    return (
      <p className="text-[13px] text-fg-muted" data-testid="destino-por-ccb-erro">
        {t.erroAoCarregar}
      </p>
    );
  }
  if (dados === null || dados.irmas.length === 0) return null;
  return (
    <fieldset className="border border-border p-3" data-testid="destino-por-ccb">
      <legend className="px-1 text-[13px] font-semibold text-fg">{t.legenda}</legend>
      <label className="flex items-center gap-2 text-[14px] text-fg">
        <input type="checkbox" checked disabled className="h-4 w-4" />
        {dados.ccb !== null ? t.esteCaso.replace('{ccb}', dados.ccb) : t.esteCasoSemCcb}
      </label>
      {dados.irmas.map((irma) => (
        <label key={irma.case_id} className="mt-1 flex items-center gap-2 text-[14px] text-fg">
          <input
            type="checkbox"
            checked={destinos.marcadas.has(irma.case_id)}
            onChange={(evt) => destinos.alternar(irma.case_id, evt.target.checked)}
            disabled={disabled}
            className="h-4 w-4"
            data-testid={`destino-por-ccb-${irma.case_id}`}
          />
          {t.irma.replace('{ccb}', irma.ccb)}
        </label>
      ))}
      <p className="mt-2 text-[13px] text-fg-muted">{t.ajuda}</p>
    </fieldset>
  );
}

/** Para onde cada lote também foi, e a nova tentativa do que falhou. */
export function EncaminhamentosPorCcb({
  destinos,
}: {
  destinos: DestinosPorCcb;
}): JSX.Element | null {
  if (destinos.encaminhamentos.length === 0) return null;
  const nomes = (ids: readonly string[]): string =>
    ids
      .map((id) => destinos.dados?.irmas.find((i) => i.case_id === id)?.ccb)
      .filter((n): n is string => n !== undefined)
      .map(rotulo)
      .join(', ');
  return (
    <ul className="space-y-1" data-testid="destino-por-ccb-envios">
      {destinos.encaminhamentos.map((e) => (
        <li
          key={e.recebimentoId}
          role={e.estado === 'falhou' ? 'alert' : 'status'}
          className={
            e.estado === 'falhou' ? 'text-[13px] text-danger' : 'text-[13px] text-fg-muted'
          }
          data-testid={`destino-por-ccb-envio-${e.recebimentoId}`}
        >
          {t[
            e.estado === 'enviando'
              ? 'encaminhando'
              : e.estado === 'feito'
                ? 'encaminhado'
                : 'falhou'
          ].replace('{ccbs}', nomes(e.destinos))}
          {e.estado === 'falhou' ? (
            <button
              type="button"
              className="ml-2 text-accent-700 underline underline-offset-2"
              onClick={() => void destinos.encaminhar(e.recebimentoId)}
            >
              {t.tentarDeNovo}
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
